/**
 * Jauge demi-cercle et anneau de progression, pour les widgets (bandeaux sombres ou cartes claires).
 */

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

interface GaugeProps {
  value: number;
  min?: number;
  max: number;
  unit: string;
  /** Libellé sous la jauge, entre le min et le max. */
  label?: string;
  color?: string;
  /** Plage normale, dessinée en vert sous l'aiguille (ex. 207–253 V). */
  band?: [number, number];
  decimals?: number;
  locale?: string;
  /** Sur fond sombre (bandeau). */
  dark?: boolean;
}

export function Gauge({ value, min = 0, max, unit, label, color = '#1e88e5', band, decimals = 1, locale = 'fr-FR', dark = false }: GaugeProps) {
  const cx = 100, cy = 100, r = 80;
  const frac = (v: number) => clamp01((v - min) / (max - min || 1));
  const pt = (f: number) => { const a = Math.PI * (1 - f); return [cx + r * Math.cos(a), cy - r * Math.sin(a)]; };
  const arc = (f0: number, f1: number) => {
    const [x0, y0] = pt(f0); const [x1, y1] = pt(f1);
    return `M${x0.toFixed(2)} ${y0.toFixed(2)} A${r} ${r} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
  };
  const f = frac(value);
  const [kx, ky] = pt(f);
  const fmt = (n: number, d = decimals) => n.toLocaleString(locale, { minimumFractionDigits: d, maximumFractionDigits: d });
  const fg = dark ? 'text-white' : 'text-zce-navy';
  const muted = dark ? 'text-white/70' : 'text-gray-500';
  return (
    <div className="relative w-[200px] h-[132px]" role="img" aria-label={`${label ?? ''} ${fmt(value)} ${unit}`}>
      <svg width="200" height="112" viewBox="0 0 200 112" aria-hidden>
        <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke={dark ? 'rgba(255,255,255,.12)' : '#e8eef6'} strokeWidth={12} strokeLinecap="round" />
        {band && <path d={arc(frac(band[0]), frac(band[1]))} fill="none" stroke={dark ? 'rgba(34,197,94,.32)' : 'rgba(34,197,94,.24)'} strokeWidth={12} />}
        <path d={arc(0, Math.max(f, 0.001))} fill="none" stroke={color} strokeWidth={12} strokeLinecap="round" />
        <circle cx={kx} cy={ky} r={7} fill={dark ? '#0b2a4d' : '#ffffff'} stroke={color} strokeWidth={3} />
      </svg>
      <div className="absolute inset-x-0 top-[54px] text-center">
        <span className={`font-mono tabular-nums text-[26px] font-semibold tracking-tight ${fg}`}>{fmt(value)}</span>
        <span className={`ml-1 text-[13px] font-semibold ${muted}`}>{unit}</span>
      </div>
      <div className={`absolute inset-x-0 bottom-0 flex justify-between px-3 font-mono text-[10px] ${muted}`}>
        <span>{fmt(min, 0)}</span>
        {label && <span className="font-sans text-[11px] font-semibold uppercase tracking-wide">{label}</span>}
        <span>{fmt(max, 0)}</span>
      </div>
    </div>
  );
}

interface RingProps {
  value: number;
  max: number;
  /** Texte au centre (ex. « 4/6 »). */
  text: string;
  caption?: string;
  color?: string;
  size?: number;
  dark?: boolean;
}

export function Ring({ value, max, text, caption, color = '#22c55e', size = 104, dark = false }: RingProps) {
  const C = 2 * Math.PI * 50;
  const f = clamp01(max > 0 ? value / max : 0);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} role="img" aria-label={`${text} ${caption ?? ''}`}>
      <svg width={size} height={size} viewBox="0 0 120 120" className="-rotate-90" aria-hidden>
        <circle cx="60" cy="60" r="50" fill="none" stroke={dark ? 'rgba(255,255,255,.12)' : '#e8eef6'} strokeWidth={11} />
        <circle cx="60" cy="60" r="50" fill="none" stroke={color} strokeWidth={11} strokeLinecap="round" strokeDasharray={`${(C * f).toFixed(1)} ${C.toFixed(1)}`} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className={`font-mono tabular-nums font-semibold leading-none ${dark ? 'text-white' : 'text-zce-navy'}`} style={{ fontSize: Math.round(size * 0.2) }}>{text}</span>
        {caption && <span className={`mt-1 text-[10px] font-semibold uppercase tracking-wide ${dark ? 'text-white/70' : 'text-gray-500'}`}>{caption}</span>}
      </div>
    </div>
  );
}
